import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch, useApp } from "../../context/AppContext";
import { Button, Modal, Select } from "./UI";
import CollapsibleSettingsCard from "./CollapsibleSettingsCard";
import { Download, FileSpreadsheet, Globe, Trash2, Upload } from "lucide-react";
import Swal from "sweetalert2";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function GlobalComplianceSettingsPanel() {
  const { showNotification } = useApp();
  const fileRef = useRef(null);
  const [selections, setSelections] = useState([]);
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const applyPayload = (data) => {
    const nextRegions = data.regions || [];
    const known = new Set(
      nextRegions.flatMap((item) => item.countries.map((country) => `${item.name}|${country}`)),
    );
    const nextSelections = (data.selections || [])
      .map((item) => (
        item.country === "India" ? { region: "Asia Pacific", country: "India" } : item
      ))
      .filter((item) => !known.size || known.has(`${item.region}|${item.country}`));
    setRegions(nextRegions);
    setSelections(nextSelections.length ? nextSelections : [{ region: "Asia Pacific", country: "India" }]);
    setRegion((current) => current || nextRegions[0]?.name || "");
    setCountry((current) => current || nextRegions[0]?.countries?.[0] || "");
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/tenant/settings/global-compliance");
      const data = await res.json();
      if (res.ok) applyPayload(data);
    } catch {
      showNotification("Could not load compliance countries", "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setSaving(true);
    try {
      const res = await apiFetch("/tenant/settings/global-compliance", {
        method: "PUT",
        body: JSON.stringify({ selections: next }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = Array.isArray(data.detail) ? data.detail[0]?.msg : data.detail;
        showNotification(detail || "Could not save countries", "error");
        return;
      }
      applyPayload(data);
      showNotification("Global compliance countries saved", "success");
    } catch {
      showNotification("Could not save countries", "error");
    } finally {
      setSaving(false);
    }
  };

  const addOne = () => {
    if (!region || !country) return;
    if (selections.some((row) => row.region === region && row.country === country)) {
      showNotification("That country is already in the list", "error");
      return;
    }
    save([...selections, { region, country }]);
  };

  const removeOne = (item) => {
    const next = selections.filter((row) => row.region !== item.region || row.country !== item.country);
    if (next.length) save(next);
  };

  /* The API treats an empty list as "reset to default", so this returns the tenant to India only. */
  const clearAll = async () => {
    const onlyDefault = selections.length === 1 && selections[0].country === "India";
    if (onlyDefault) return;
    const { isConfirmed } = await Swal.fire({
      title: "Clear all countries?",
      text: "All selected countries will be removed and the list will reset to the default (India).",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, clear all",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
      focusCancel: true,
    });
    if (isConfirmed) save([]);
  };

  const downloadTemplate = () => {
    const lines = ["region,country"];
    regions.forEach((item) => {
      item.countries.forEach((name) => lines.push(`${item.name},${name}`));
    });
    const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "global-compliance-template.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const onUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setSaving(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await apiFetch("/tenant/settings/global-compliance/bulk", { method: "POST", body });
      const queued = await res.json();
      if (!res.ok) {
        showNotification(queued.detail || "Could not queue the upload", "error");
        return;
      }
      for (let attempt = 0; attempt < 30; attempt += 1) {
        await sleep(1000);
        const statusRes = await apiFetch(`/tenant/settings/global-compliance/bulk/${queued.task_id}`);
        const status = await statusRes.json();
        if (status.status === "success") {
          if (status.selections?.length) setSelections(status.selections);
          const note = status.skipped ? `Added ${status.added}. Skipped ${status.skipped} invalid row(s).` : `Added ${status.added} countries.`;
          showNotification(note, status.skipped ? "error" : "success");
          return;
        }
        if (status.status === "failed") {
          showNotification(status.error || "Bulk upload failed", "error");
          return;
        }
      }
      showNotification("Upload is still running. Refresh this page in a moment.", "error");
    } catch {
      showNotification("Could not upload the file", "error");
    } finally {
      setSaving(false);
      setBulkOpen(false);
    }
  };

  const countries = regions.find((item) => item.name === region)?.countries || [];
  const grouped = regions
    .map((item) => ({ name: item.name, countries: selections.filter((row) => row.region === item.name) }))
    .filter((group) => group.countries.length);

  return (
    <CollapsibleSettingsCard
      tourId="tour-tenant-settings-global-compliance"
      title="Global Compliance"
      description="Choose the regions and countries your users see on the Global Compliance page. India is the default."
      icon={<Globe size={18} className="text-white" />}
      iconClassName="bg-gradient-to-br from-sky-500 to-blue-600"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Select label="Region" value={region} onChange={(e) => {
          const nextRegion = e.target.value;
          const nextCountries = regions.find((item) => item.name === nextRegion)?.countries || [];
          setRegion(nextRegion);
          setCountry(nextCountries[0] || "");
        }}>
          {regions.map((item) => (
            <option key={item.name} value={item.name}>{item.name}</option>
          ))}
        </Select>
        <Select label="Country" value={country} onChange={(e) => setCountry(e.target.value)} disabled={!region}>
          {countries.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </Select>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={addOne} loading={saving} disabled={loading || !country}>Add country</Button>
        <Button variant="secondary" onClick={() => setBulkOpen(true)} disabled={saving || loading}>
  <Upload size={14} /> Bulk upload
</Button>
        <Button
          variant="secondary"
          onClick={clearAll}
          disabled={saving || loading || (selections.length === 1 && selections[0].country === "India")}
        >
          <Trash2 size={14} /> Clear all
        </Button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onUpload} />
        <Modal open={bulkOpen} onClose={() => !saving && setBulkOpen(false)} title="Bulk upload countries" size="sm">
          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                <FileSpreadsheet size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900">1. Get the template</p>
                <p className="text-xs text-slate-600">Keep only the rows you need, then save.</p>
              </div>
              <Button size="xs" variant="secondary" onClick={downloadTemplate} disabled={!regions.length}>
                <Download size={12} /> Template
              </Button>
            </div>

            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={saving}
              className="flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed border-slate-300 px-4 py-6 text-center transition-colors hover:border-blue-400 hover:bg-blue-50/50 disabled:opacity-60"
            >
              {saving ? (
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
              ) : (
                <Upload size={20} className="text-blue-600" />
              )}
              <span className="text-sm font-semibold text-slate-900">
                {saving ? "Importing…" : "2. Choose your CSV file"}
              </span>
              <span className="text-xs text-slate-600">Columns: region,country · imported in the background</span>
            </button>
          </div>
        </Modal>
      </div>
      {/* <p className="mt-2 text-xs font-medium text-slate-700">
        Download the template, delete the rows you don't need, then upload it. Columns: region,country. The file is imported in the background.
      </p> */}
      <div className="mt-4 space-y-3">
        {loading && <p className="text-sm font-medium text-slate-700">Loading countries…</p>}
        {!loading && grouped.map((group) => (
          <div key={group.name} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-800">{group.name}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {group.countries.map((item) => (
                <button
                  key={`${item.region}-${item.country}`}
                  type="button"
                  onClick={() => removeOne(item)}
                  className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-slate-900 border border-blue-200 hover:border-red-200 hover:text-red-700"
                  title="Remove"
                >
                  {item.country} ×
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </CollapsibleSettingsCard>
  );
}